import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { toLatinDigits } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Stack } from "@wato/ui";
import { isApiError } from "../../api/client";
import { useApi, useErrorMessage } from "../../api/context";
import { useAuth } from "../../auth/AuthProvider";
import { AuthLayout, safeNextPath } from "../../components/common";

/**
 * Second sign-in step (ADR-0013). After a password login the challenge comes in router state;
 * after "Continue with Google" it is the short-lived `wato_2fa` cookie, so no token is needed.
 * After 5 wrong codes the API locks code entry and offers backup codes.
 */
export function TwoFactorLoginPage() {
  const { t } = useI18n();
  const api = useApi();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const errorMessage = useErrorMessage();
  const challengeToken = (location.state as { challengeToken?: string } | null)?.challengeToken;
  const [mode, setMode] = useState<"code" | "backup">("code");
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const entered = toLatinDigits(value).trim();
    try {
      await api.loginSecondFactor({
        ...(challengeToken ? { challengeToken } : {}),
        ...(mode === "code" ? { code: entered } : { backupCode: entered }),
      });
      await refresh();
      await navigate(safeNextPath(params.get("next")) ?? "/", { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
      if (isApiError(caught) && caught.details.backupCodeAllowed === true) setMode("backup");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("dashboard.auth.twoFactor.title")}
      subtitle={t(
        mode === "code"
          ? "dashboard.auth.twoFactor.subtitle"
          : "dashboard.auth.twoFactor.backupSubtitle",
      )}
      footer={
        <Link className="font-semibold text-on-action-ghost underline" to="/login">
          {t("dashboard.auth.backToLogin")}
        </Link>
      }
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {error && <Alert tone="error">{error}</Alert>}
          {mode === "code" ? (
            <Input
              key="code"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              required
              label={t("dashboard.auth.twoFactor.code")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          ) : (
            <Input
              key="backup"
              dir="ltr"
              autoComplete="off"
              required
              label={t("dashboard.auth.twoFactor.backupCode")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          )}
          <Button type="submit" fullWidth loading={busy}>
            {t("dashboard.auth.twoFactor.submit")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setMode(mode === "code" ? "backup" : "code");
              setValue("");
            }}
          >
            {t(
              mode === "code"
                ? "dashboard.auth.twoFactor.useBackup"
                : "dashboard.auth.twoFactor.useCode",
            )}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
